import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { UserInitialStateType } from "DataTypes/Redux";
import { onReceiveNotification, offReceiveNotification, initializeChatHub, onReceiveGlobalMessage, onReceiveGlobalSOS, offReceiveGlobalSOS, onReceiveCallAnswered, offReceiveCallAnswered, onReceiveCallEnded, offReceiveCallEnded, onReceiveCallRejected, offReceiveCallRejected } from "../../Service/signalRService";
import notificationApi from "../../Service/Notification.api";
import { useNavigate } from "react-router-dom";
import { RouteUrls } from "Constants/RouteUrls";
import { Dialog, Typography, Box, Button, Avatar, Stack, IconButton, keyframes, CircularProgress } from "@mui/material";
import { Call as CallIcon, CallEnd as CallEndIcon, Videocam as VideocamIcon } from "@mui/icons-material";
import AgoraCall from "Components/Conference/AgoraCall";
import { MeetingApi } from "Service/Meeting.api";

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7); }
  70% { box-shadow: 0 0 0 20px rgba(16, 185, 129, 0); }
  100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
`;

export interface Notification {
    id: number;
    recipientId: number;
    userType: string;
    title: string;
    message: string;
    link?: string;
    isRead: boolean;
    createdAt: string;
    metadata?: string;
    isLocal?: boolean;
    isCall?: boolean;
}

interface NotificationContextType {
    notifications: Notification[];
    unreadCount: number;
    loading: boolean;
    markAsRead: (id: number) => Promise<void>;
    markAllAsRead: () => Promise<void>;
    fetchNotifications: (page?: number) => Promise<void>;
    activeCaseId?: number | null;
    setActiveCaseId?: (id: number | null) => void;
    markCaseNotificationsAsRead?: (caseId: number) => Promise<void>;
    globalCallData?: any;
    setGlobalCallData?: (data: any) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [notificationSound] = useState(new Audio("/Assets/audio/notification-alert.mp3"));
    const [callSound] = useState(new Audio("/Assets/audio/skype_call.mp3"));
    const [incomingCallData, setIncomingCallData] = useState<Notification | null>(null);
    const [globalCallData, setGlobalCallData] = useState<any>(null);
    const [activeCaseId, setActiveCaseId] = useState<number | null>(null);
    const activeCaseIdRef = React.useRef<number | null>(null);
    const lastSoundTimeRef = React.useRef<number>(0);
    const recentNotifKeysRef = React.useRef<Set<string>>(new Set());
    const notificationsRef = React.useRef<Notification[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        activeCaseIdRef.current = activeCaseId;
    }, [activeCaseId]);

    useEffect(() => {
        notificationsRef.current = notifications;
    }, [notifications]);

    const { myInfo }: UserInitialStateType = useSelector((state: any) => state?.user);
    const isAuthenticated = !!myInfo;
    const fetchNotifications = useCallback(async (page = 1) => {
        if (!isAuthenticated) return;
        setLoading(true);
        try {
            const response = await notificationApi.fetchNotifications(page);
            if (response.success) {
                const normalizedNotifications = response.data.map((n: any) => ({
                    ...n,
                    id: Number(n.id || n.Id),
                    isRead: n.isRead ?? n.IsRead ?? false,
                    link: n.link || n.Link,
                    metadata: n.metadata || n.Metadata,
                    title: n.title || n.Title,
                    message: n.message || n.Message,
                    createdAt: n.createdAt || n.CreatedAt
                }));
                setNotifications(normalizedNotifications.filter((n: any) => !isNaN(n.id)));
            }

            const countResponse = await notificationApi.getUnreadCount();
            if (countResponse.success) {
                setUnreadCount(countResponse.data.count);
            }
        } catch (error) {
            console.error("Failed to fetch notifications", error);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    const markAsRead = async (id: number) => {
        try {
            // Optimistic update
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));

            const targetNote = notifications.find(n => n.id === id);

            if (targetNote && !targetNote.isLocal) {
                await notificationApi.markAsRead(id);
            }

            try {
                const existing = localStorage.getItem("unread_notifications");
                if (existing) {
                    const notes = JSON.parse(existing);
                    const filtered = notes.filter((n: any) => Number(n.id) !== id);
                    localStorage.setItem("unread_notifications", JSON.stringify(filtered));
                    window.dispatchEvent(new Event("notificationsUpdated"));
                }
            } catch (e) {
                console.error("Error clearing local storage notification", e);
            }

        } catch (error) {
            console.error("Failed to mark notification as read", error);
        }
    };

    // useCallback + a ref for reading current notifications (instead of closing over the
    // `notifications` state directly) keeps this function's identity stable across renders.
    // Without that, every call updates state (fetchNotifications below), which re-renders this
    // provider, which would otherwise hand consumers a new function reference each time —
    // and index1.tsx's useEffect depends on this function, so an unstable reference there
    // re-fires the effect, calls this again, and loops.
    const markCaseNotificationsAsRead = useCallback(async (caseId: number) => {
        // Find all unread notifications for this case that we already know about locally
        const targetNotifications = notificationsRef.current.filter(n => {
            if (n.isRead) return false;
            let meta: any = {};
            try {
                meta = n.metadata ? (typeof n.metadata === 'string' ? JSON.parse(n.metadata) : n.metadata) : {};
            } catch (e) { return false; }
            return (meta.CaseId || meta.caseId) == caseId;
        });

        if (targetNotifications.length > 0) {
            const ids = targetNotifications.map(n => n.id);

            // Optimistic update for immediate UI feedback
            setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, isRead: true } : n));
            setUnreadCount(prev => Math.max(0, prev - ids.length));

            try {
                const existing = localStorage.getItem("unread_notifications");
                if (existing) {
                    const notes = JSON.parse(existing);
                    const filtered = notes.filter((n: any) => !ids.includes(Number(n.id)));
                    localStorage.setItem("unread_notifications", JSON.stringify(filtered));
                    window.dispatchEvent(new Event("notificationsUpdated"));
                }
            } catch (e) {
                console.error("Error batch clearing local storage", e);
            }
        }

        // Always sync with the server, even when nothing matched locally — this tab's
        // `notifications` state can be stale (a notification that landed after the last
        // poll/SignalR event, or a page reload between a message being sent and its
        // notification arriving here). Previously this function returned early when the local
        // list had nothing to mark, which meant that notification could never be cleared
        // server-side, permanently stranding the sidebar's unread badge. Refetching afterward
        // re-syncs local state with whatever the server actually has.
        try {
            await notificationApi.markCaseNotificationsAsRead(caseId);
            fetchNotifications();
        } catch (e: any) {
            console.error(`Failed to mark read for case ${caseId}`, e);
        }
    }, [fetchNotifications]);

    const markAllAsRead = async () => {
        try {
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
            localStorage.removeItem("unread_notifications");
            window.dispatchEvent(new Event("notificationsUpdated"));

            await notificationApi.markAllAsRead();
        } catch (error) {
            console.error("Failed to mark all as read", error);
        }
    };

    useEffect(() => {
        if ("Notification" in window && window.Notification.permission === "default") {
            window.Notification.requestPermission().catch(e => console.warn("Notification permission error:", e));
        }
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            fetchNotifications();
        }
    }, [isAuthenticated, fetchNotifications]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const handleNewNotification = async (notification: any) => {
            const newNotification: Notification = {
                ...notification,
                id: Number(notification.id)
            };

            const currentActiveCase = activeCaseIdRef.current;

            let meta: any = {};
            try {
                meta = notification.metadata ? (typeof notification.metadata === 'string' ? JSON.parse(notification.metadata) : notification.metadata) : {};
            } catch (e) { console.error("Metadata parse error", e); }

            const notifCaseId = meta.CaseId || meta.caseId;
            const senderId = meta.SenderId || meta.senderId;

            // Don't show notification to the user who initiated the action (e.g. initiated the call)
            if (senderId && myInfo?.id && Number(senderId) === Number(myInfo.id)) {
                return;
            }

            if (currentActiveCase && notifCaseId && Number(currentActiveCase) === Number(notifCaseId)) {
                // User is in this chat! Mark as read silently.
                markAsRead(newNotification.id);
                return;
            }

            // Deduplicate notifications received via both ReceiveNotification & ReceiveMessageGlobal within 3s
            const notifKey = `${notifCaseId}_${newNotification.message}`;
            if (notifCaseId && recentNotifKeysRef.current.has(notifKey)) {
                return; // Duplicate event, skip second increment
            }
            if (notifCaseId) {
                recentNotifKeysRef.current.add(notifKey);
                setTimeout(() => { recentNotifKeysRef.current.delete(notifKey); }, 3000);
            }
            // -----------------------

            // Check if we already have this notification to prevent duplicates
            setNotifications(prev => {
                if (prev.some(n => n.id === newNotification.id)) return prev;
                // Safely increment unread count only if we are actually adding a new notification
                setUnreadCount(count => count + 1);
                return [newNotification, ...prev];
            });

            // --- LEGACY SUPPORT ---
            try {
                const existing = localStorage.getItem("unread_notifications");
                let notes = existing ? JSON.parse(existing) : [];
                if (!notes.some((n: any) => n.id === newNotification.id)) {
                    notes.push(newNotification);
                    localStorage.setItem("unread_notifications", JSON.stringify(notes));
                    window.dispatchEvent(new Event("notificationsUpdated"));
                }
            } catch (e) {
                console.error("Error updating local storage notifications", e);
            }

            // Play sound using the stable instance with 1.5s debouncing to prevent double ringing
            try {
                // BUG: this used to also match `title`/`message` containing the substring "call" —
                // so a completely ordinary chat message whose text happened to say "call" (e.g.
                // "please call the client") triggered the full incoming-call ringtone + Accept/
                // Decline popup for Admin, even though nobody placed an Agora call. Only trust the
                // explicit IsCall flag the backend actually sets for real calls: newNotification.isCall
                // (set correctly by the onReceiveGlobalMessage handler above from data.isCall/IsCall)
                // or meta.IsCall/meta.isCall (set by SendCaseNotificationAsync's ReceiveMessageGlobal
                // payload and by SendNotificationAsync's metadata for the direct ReceiveNotification
                // path) — never a heuristic on message text.
                const isCall = newNotification.isCall === true || meta.IsCall === true || meta.isCall === true;
                if (isCall) {
                    if (notifCaseId) {
                        try {
                            const statusData = await MeetingApi.GetCallStatus(notifCaseId);
                            if (statusData && (statusData.isCallEnded === true || statusData.IsCallEnded === true)) {
                                console.log("[Website] Database confirms call is ALREADY ENDED. Suppressing incoming call popup.");
                                return;
                            }
                        } catch (err) {
                            console.warn("[Website] Call status check warning:", err);
                        }
                    }

                    callSound.currentTime = 0;
                    callSound.loop = true;
                    callSound.play().catch((e: any) => console.warn("Audio play blocked", e));
                    setIncomingCallData(newNotification);
                    
                    if ("Notification" in window && window.Notification.permission === "granted" && document.hidden) {
                        try {
                            const nativeNotif = new window.Notification(newNotification.title, {
                                body: newNotification.message,
                                requireInteraction: true,
                            });
                            nativeNotif.onclick = () => {
                                window.focus();
                                nativeNotif.close();
                            };
                        } catch(e) {
                            console.warn("Native notification failed:", e);
                        }
                    }

                    return; // Skip toast for calls, show modal instead
                } else {
                    const now = Date.now();
                    if (now - lastSoundTimeRef.current > 1500) {
                        lastSoundTimeRef.current = now;
                        notificationSound.currentTime = 0;
                        notificationSound.play().catch((e: any) => console.warn("Audio play blocked", e));
                    }
                }
            } catch (error) {
                console.error("Audio error", error);
            }

            toast.info(newNotification.title + ": " + newNotification.message, {
                onClick: () => {
                    let meta: any = {};
                    try {
                        meta = newNotification.metadata ? (typeof newNotification.metadata === 'string' ? JSON.parse(newNotification.metadata) : newNotification.metadata) : {};
                    } catch (e) { }
                    const orderTypeId = meta.OrderTypeId || meta.orderTypeId || (newNotification as any).orderTypeId || (newNotification as any).OrderTypeId;

                    if (newNotification.link) {
                        const chatMatch = newNotification.link.match(/(?:^|\/)chat\/(\d+)/i);
                        if (chatMatch && chatMatch[1]) {
                            const cId = parseInt(chatMatch[1]);
                            const basePath = Number(orderTypeId) === 53 ? '/chat/admin-support' : '/chat';
                            navigate(`${basePath}?openCase=${cId}`);
                            return;
                        }
                        if (!newNotification.link.includes("/api/") && !newNotification.link.includes("messages")) {
                            const targetLink = newNotification.link.startsWith('/') ? newNotification.link : `/${newNotification.link}`;
                            navigate(targetLink);
                            return;
                        }
                    }
                    const caseId = meta.CaseId || meta.caseId;
                    if (caseId) {
                        const basePath = orderTypeId === 53 ? '/chat/admin-support' : '/chat';
                        navigate(`${basePath}?openCase=${caseId}`);
                    }
                }
            });
        };

        const setupSignalR = async () => {
            try {
                await initializeChatHub(localStorage.getItem("token") || "");

                // 1. Direct Notifications
                onReceiveNotification(handleNewNotification);

                // 2. Global Messages (Active Chats)
                onReceiveGlobalMessage((data: any) => {
                    window.dispatchEvent(new CustomEvent("globalMessageReceived", { detail: data }));
                    window.dispatchEvent(new Event("unreadCountUpdated"));

                    // Don't show notification popup if user is already looking at this chat
                    const currentPath = window.location.pathname;
                    if (currentPath.includes(`/Chat/${data.caseId}`)) return;

                    // Support both camelCase and PascalCase
                    const backendId = data.notificationId || data.NotificationId;

                    const newNote: Notification = {
                        id: backendId || Date.now(), // Use backend ID if available
                        recipientId: 0,
                        userType: "User",
                        title: data.title || data.Title || "💬 New Message",
                        message: data.isCall || data.IsCall ? (data.content || data.Content) : `Case #${data.caseId}: ${data.content || data.Content}`,
                        link: data.link || data.Link || `/chat?openCase=${data.caseId}`,
                        isRead: false,
                        createdAt: new Date().toISOString(),
                        metadata: JSON.stringify({ CaseId: data.caseId, SenderId: data.senderUserId || data.SenderUserId }),
                        isLocal: !backendId, // Flag as local if no server ID provided
                        isCall: data.isCall || data.IsCall || false
                    };
                    handleNewNotification(newNote);
                });

                // 3. SOS Alerts (Admin)
                if (myInfo?.userType === "ADMIN" || myInfo?.userType === "Admin") {
                    onReceiveGlobalSOS((data: any) => {
                        const newNote: Notification = {
                            id: Date.now(),
                            recipientId: 0,
                            userType: "Admin",
                            title: "🚨 SOS URGENT",
                            message: `Case #${data.caseId} needs help!`,
                            link: `/chat?openCase=${data.caseId}`,
                            isRead: false,
                            createdAt: new Date().toISOString(),
                            metadata: JSON.stringify({ CaseId: data.caseId }),
                            isLocal: true
                        };
                        handleNewNotification(newNote);
                    });
                }

                // 4. Stop this popup's ringing the moment the call is answered/ended/rejected
                // anywhere — CallAnswered (same user, another of their own devices/tabs joined),
                // CallEnded, and CallRejected. Previously this popup had NO event-driven stop
                // path at all: it only paused callSound when the user clicked Accept/Decline
                // themselves, so it kept ringing indefinitely if the call was answered elsewhere
                // (e.g. on mobile) or ended/rejected by the other party.
                const dismissIncomingCallPopup = () => {
                    callSound.pause();
                    callSound.currentTime = 0;
                    setIncomingCallData(null);
                };
                onReceiveCallAnswered(dismissIncomingCallPopup);
                onReceiveCallEnded(dismissIncomingCallPopup);
                onReceiveCallRejected(dismissIncomingCallPopup);

            } catch (error) {
                console.error("Error initializing SignalR for notifications:", error);
            }
        };

        setupSignalR();

        return () => {
            offReceiveNotification();
            offReceiveGlobalSOS();
            offReceiveCallAnswered();
            offReceiveCallEnded();
            offReceiveCallRejected();
        };
    }, [isAuthenticated]); // Removed activeCaseId from dependency array

    return (
        <NotificationContext.Provider value={{ notifications, unreadCount, loading, markAsRead, markAllAsRead, fetchNotifications, activeCaseId, setActiveCaseId, markCaseNotificationsAsRead, globalCallData, setGlobalCallData }}>
            {children}
            {incomingCallData && (
                <Dialog 
                    open={true} 
                    maxWidth="xs" 
                    fullWidth 
                    PaperProps={{ 
                        sx: { 
                            borderRadius: 6, 
                            textAlign: 'center', 
                            p: 4, 
                            background: 'linear-gradient(145deg, #1a237e 0%, #283593 100%)', 
                            color: 'white',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                        } 
                    }}
                >
                    <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <Avatar 
                            sx={{ 
                                width: 80, 
                                height: 80, 
                                bgcolor: 'rgba(255, 255, 255, 0.1)', 
                                mb: 1,
                                border: '2px solid rgba(255, 255, 255, 0.2)'
                            }}
                        >
                            {incomingCallData.title?.includes("Video") ? (
                                <VideocamIcon sx={{ fontSize: 40 }} />
                            ) : (
                                <CallIcon sx={{ fontSize: 40 }} />
                            )}
                        </Avatar>
                        
                        <Typography variant="h5" fontWeight="800" sx={{ color: 'white' }}>
                            {incomingCallData.title}
                        </Typography>
                        
                        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 4 }}>
                            {incomingCallData.message}
                        </Typography>

                        <Stack direction="row" spacing={4} mt={2}>
                            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                <IconButton 
                                    onClick={() => {
                                        callSound.pause();
                                        callSound.currentTime = 0;
                                        if (incomingCallData) {
                                            let meta: any = {};
                                            try { meta = incomingCallData.metadata ? (typeof incomingCallData.metadata === 'string' ? JSON.parse(incomingCallData.metadata) : incomingCallData.metadata) : {}; } catch (e) { }
                                            const caseId = meta.CaseId || meta.caseId;
                                            const messageId = meta.MessageId || meta.messageId;
                                            const currentUserId = localStorage.getItem("x-nlp-userid") || "0";
                                            if (caseId && messageId) {
                                                MeetingApi.RejectAgoraConferenceCall({
                                                    caseId: Number(caseId),
                                                    messageId: Number(messageId),
                                                    rejectedByUserId: Number(currentUserId)
                                                }).catch(console.error);
                                            }
                                        }
                                        setIncomingCallData(null);
                                    }}
                                    sx={{ 
                                        bgcolor: '#ef4444', 
                                        color: 'white',
                                        width: 64,
                                        height: 64,
                                        transition: 'all 0.2s ease',
                                        '&:hover': { bgcolor: '#dc2626', transform: 'scale(1.05)' }
                                    }}
                                >
                                    <CallEndIcon sx={{ fontSize: 32 }} />
                                </IconButton>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'white' }}>Decline</Typography>
                            </Box>

                            <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                                <IconButton 
                                    onClick={() => {
                                        callSound.pause();
                                        callSound.currentTime = 0;
                                        setIncomingCallData(null);
                                        let meta: any = {};
                                        try { meta = incomingCallData.metadata ? (typeof incomingCallData.metadata === 'string' ? JSON.parse(incomingCallData.metadata) : incomingCallData.metadata) : {}; } catch (e) { }

                                        const isVideoCall = (incomingCallData as any).isVideoCall === true || 
                                                            meta.IsVideoCall === true || 
                                                            meta.isVideoCall === true || 
                                                            meta.isVideo === true || 
                                                            incomingCallData.title?.toLowerCase().includes("video") || 
                                                            incomingCallData.message?.includes("📹") || 
                                                            incomingCallData.message?.toLowerCase().includes("video") || 
                                                            false;
                                        
                                        const orderTypeId = meta.OrderTypeId || meta.orderTypeId || (incomingCallData as any).orderTypeId || (incomingCallData as any).OrderTypeId;
                                        const caseId = meta.CaseId || meta.caseId;
                                        let targetCaseId = caseId || orderTypeId;

                                        if (incomingCallData.link) {
                                            const chatMatch = incomingCallData.link.match(/(?:^|\/)chat\/(\d+)\/messages(?:\?(.+))?/i);
                                            if (chatMatch && chatMatch[1]) {
                                                targetCaseId = parseInt(chatMatch[1]);
                                            }
                                        }

                                        if (targetCaseId) {
                                            // 1. INSTANTLY open root-level global call modal (<10ms)
                                            setGlobalCallData({
                                                open: true,
                                                loading: true,
                                                isVideoCall: isVideoCall,
                                                caseId: targetCaseId
                                            });

                                            // 2. Fetch Agora Token IMMEDIATELY in root manager
                                            const currentUserId = localStorage.getItem("x-nlp-userid") || "0";
                                            MeetingApi.AgoraConferenceCall({
                                                caseId: String(targetCaseId),
                                                isJoinOnly: true,
                                                isVideoCall: isVideoCall
                                            }).then((res: any) => {
                                                if (res?.success && (res.data || res.appId)) {
                                                    const payload = res.data || res;
                                                    const finalIsVideo = payload.isVideoCall ?? isVideoCall;
                                                    setGlobalCallData({
                                                        open: true,
                                                        loading: false,
                                                        appId: payload.appId,
                                                        channelName: payload.channelName,
                                                        token: payload.token,
                                                        uid: Number(payload.uid || currentUserId || 0),
                                                        messageId: payload.messageId,
                                                        participants: payload.participants || [],
                                                        isVideoCall: finalIsVideo
                                                    });
                                                } else {
                                                    setGlobalCallData(null);
                                                    toast.error(res?.message || "Failed to join call");
                                                }
                                            }).catch((err: any) => {
                                                setGlobalCallData(null);
                                                console.error("Global call error:", err);
                                            });

                                            // 3. Concurrently navigate to chat route in background
                                            const basePath = Number(orderTypeId) === 53 ? '/chat/admin-support' : '/chat';
                                            navigate(`${basePath}?openCase=${targetCaseId}`);
                                        }
                                    }}
                                    sx={{ 
                                        bgcolor: '#10b981', 
                                        color: 'white',
                                        width: 64,
                                        height: 64,
                                        animation: `${pulseAnimation} 2s infinite`,
                                        transition: 'all 0.2s ease',
                                        '&:hover': { bgcolor: '#059669', transform: 'scale(1.05)' }
                                    }}
                                >
                                    {incomingCallData.title?.includes("Video") ? (
                                        <VideocamIcon sx={{ fontSize: 32 }} />
                                    ) : (
                                        <CallIcon sx={{ fontSize: 32 }} />
                                    )}
                                </IconButton>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'white' }}>Accept</Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Dialog>
            )}

            {globalCallData?.open && (
                globalCallData.loading ? (
                    <Dialog open={true} maxWidth="xs" fullWidth PaperProps={{ sx: { p: 4, textAlign: 'center', borderRadius: 4, background: '#1e293b', color: 'white' } }}>
                        <CircularProgress color="inherit" sx={{ mb: 2 }} />
                        <Typography variant="h6" fontWeight="bold">Connecting to Call...</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>Joining voice/video room</Typography>
                    </Dialog>
                ) : (
                    <AgoraCall
                        appId={globalCallData.appId}
                        channelName={globalCallData.channelName}
                        token={globalCallData.token}
                        uid={globalCallData.uid}
                        participants={globalCallData.participants}
                        onClose={() => setGlobalCallData(null)}
                        onEndCall={async () => {
                            if (globalCallData.messageId) {
                                await MeetingApi.EndAgoraConferenceCall({
                                    messageId: globalCallData.messageId,
                                    duration: "00:01"
                                }).catch(console.error);
                            }
                            setGlobalCallData(null);
                        }}
                        isVideoCall={globalCallData.isVideoCall}
                    />
                )
            )}
        </NotificationContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error("useNotifications must be used within a NotificationProvider");
    }
    return context;
};
